import React from 'react';
import AssistantAnalysisChart from './AssistantAnalysisChart';

function FileSourceList({ sources }) {
  if (!sources?.length) return null;
  return (
    <div className="vp-asst-file-sources">
      {sources.map((source, index) => (
        <div key={`${source.label}-${index}`} className={source.error ? 'vp-asst-source-error' : 'vp-asst-file-source'}>
          {source.error ? `來源無法使用：${source.error}` : `檔案來源：${source.label}`}
        </div>
      ))}
    </div>
  );
}

function DownloadButton({ testId, path, name, onDownload, children }) {
  if (!onDownload || !(path || name)) return null;
  return (
    <button
      type="button"
      className="vp-asst-download"
      data-testid={testId}
      data-href={path || ''}
      data-name={name || ''}
      onClick={() => onDownload({ downloadUrl: path, name, id: undefined })}
    >
      {children}
    </button>
  );
}

function ArtifactDownload({ artifact, onDownload }) {
  if (!artifact?.downloadUrl && !artifact?.id) return null;
  return (
    <div className="vp-asst-artifact">
      <DownloadButton
        testId="assistant-artifact-download"
        path={artifact.downloadUrl}
        name={artifact.name}
        onDownload={onDownload}
      >
        下載 {artifact.name || '產物'}
      </DownloadButton>
      <p className="vp-asst-note">這是依輸入內容產出的檔案，不是已查證的研究事實。</p>
    </div>
  );
}

function formatStatValue(value) {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return Object.entries(value)
      .filter(([, v]) => v != null)
      .map(([k, v]) => `${k}=${v}`)
      .join('，');
  }
  return String(value);
}

function AnalysisBlock({ analysis }) {
  if (!analysis) return null;
  const { columns, rows, statistics, chart, summary, rowCount } = analysis;
  return (
    <div className="vp-asst-analysis" data-testid="assistant-analysis-block">
      {summary ? <p className="vp-asst-analysis-summary">{summary}</p> : null}
      {Number.isFinite(rowCount) && !rows.length ? <p className="vp-muted">列數：{rowCount}</p> : null}
      {chart ? <AssistantAnalysisChart chart={chart} /> : null}
      {columns.length && rows.length ? (
        <div className="vp-asst-table-wrap">
          <table className="vp-asst-table">
            <thead>
              <tr>{columns.map((col) => <th key={col}>{col}</th>)}</tr>
            </thead>
            <tbody>
              {rows.slice(0, 20).map((row, ri) => {
                const cells = Array.isArray(row) ? row : columns.map((col) => (row && row[col] != null ? row[col] : ''));
                return (
                  <tr key={ri}>
                    {cells.map((cell, ci) => <td key={ci}>{cell == null ? '' : String(cell)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {statistics && Object.keys(statistics).length ? (
        <ul className="vp-asst-stats">
          {Object.entries(statistics).slice(0, 12).map(([key, value]) => (
            <li key={key}>{key}：{formatStatValue(value)}</li>
          ))}
        </ul>
      ) : null}
      <FileSourceList sources={analysis.sources} />
    </div>
  );
}

export default function AssistantMessageExtras({ message, onDownload }) {
  if (!message) return null;
  return (
    <>
      {message.researchNotice ? <div className="vp-asst-research-meta">{message.researchNotice}</div> : null}
      <FileSourceList sources={message.fileSources} />
      {message.attachments?.length ? (
        <div className="vp-asst-msg-files">
          {message.attachments.map((file) => (
            file.downloadUrl || file.id
              ? (
                <DownloadButton
                  key={file.id || file.name}
                  testId="assistant-message-file-download"
                  path={file.downloadUrl}
                  name={file.name}
                  onDownload={onDownload}
                >
                  {file.name}
                </DownloadButton>
              )
              : <span key={file.name}>{file.name}</span>
          ))}
        </div>
      ) : null}
      <AnalysisBlock analysis={message.analysis} />
      <ArtifactDownload artifact={message.artifact} onDownload={onDownload} />
      {message.image?.url ? (
        <div className="vp-asst-image">
          <DownloadButton
            testId="assistant-image-download"
            path={message.image.url}
            name={message.image.name || 'image'}
            onDownload={onDownload}
          >
            下載圖像 {message.image.name || ''}
          </DownloadButton>
        </div>
      ) : null}
    </>
  );
}
